import {
    ChangeDetectionStrategy,
    Component,
    input,
    output,
    HostListener,
    ElementRef,
    computed,
    signal,
    model,
    inject,
    ViewChild,
    AfterViewChecked,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { ControlValueAccessor, FormsModule, NG_VALUE_ACCESSOR } from '@angular/forms';
import { FlooriControl } from '../../utils';
import { FlooriIconComponent } from '../floori-icon/floori-icon.component';
import { SelectOption } from '../../models';
import { DropdownPosition } from './dropdown-position.enum';
import { FlooriCheckboxComponent } from '../floori-checkbox';
import { MultiselectNamePipe } from './multiselectName/multiselect-name.pipe';
import { FlooriButtonComponent } from '../floori-button';
import { TranslatePipe, TranslationsModule } from '../../modules';

@Component({
    selector: 'floori-select',
    standalone: true,
    imports: [
        CommonModule,
        FormsModule,
        FlooriIconComponent,
        FlooriCheckboxComponent,
        FlooriButtonComponent,
        TranslationsModule,
    ],
    templateUrl: './floori-select.component.html',
    styleUrl: './floori-select.component.scss',
    providers: [
        MultiselectNamePipe,
        {
            provide: NG_VALUE_ACCESSOR,
            multi: true,
            useExisting: FlooriSelectComponent,
        },
    ],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FlooriSelectComponent
    extends FlooriControl
    implements ControlValueAccessor, AfterViewChecked
{
    private readonly dropdownHeight = 450;
    private readonly multiselectNamePipe = inject(MultiselectNamePipe);
    private readonly translatePipe = inject(TranslatePipe);

    // select inputs
    readonly options = input.required<SelectOption[]>();
    readonly restrictedOptions = input<SelectOption[] | undefined>(undefined);
    readonly placeholder = input<string>('');
    readonly multiselect = input(false);
    readonly maxDisplayedItems = input<number>(4);
    readonly selectionLimit = input<number | null>(null);

    // search only inputs
    readonly isSearch = input(false);
    readonly noResultMessage = input('');
    readonly selectMessage = input('');
    readonly showValue = input(false);
    readonly showDescription = input(false);
    readonly isRequired = input(false);
    readonly isInForm = input(false);
    readonly showSelectAllActions = input(false);
    readonly moreTranslationKey = input('common.more');

    readonly selectionChange = output<unknown>();
    readonly opened = output<boolean>();

    readonly searchQuery = model<string>('');
    readonly isOpened = signal(false);

    private _value: unknown = this.multiselect() ? [] : '';
    private _hasAutoSelected = false;

    @ViewChild('dropdown') dropdownElement: ElementRef | null = null;

    get availableOptions(): SelectOption[] {
        const baseOptions = this.restrictedOptions() ?? this.options();

        if (!this.restrictedOptions()) {
            return baseOptions;
        }

        const restrictedValues = new Set(baseOptions.map(opt => opt.value));
        const additionalOptions: SelectOption[] = [];

        const optionsMap = new Map(this.options().map(opt => [opt.value, opt]));

        if (this.multiselect() && this.isArray(this._value)) {
            for (const selectedItem of this._value) {
                const optionValue =
                    typeof selectedItem === 'object' && 'value' in selectedItem
                        ? selectedItem.value
                        : selectedItem;

                if (!restrictedValues.has(optionValue)) {
                    const fullOption = optionsMap.get(optionValue);
                    if (
                        fullOption &&
                        !additionalOptions.some(opt => opt.value === fullOption.value)
                    ) {
                        additionalOptions.push(fullOption);
                    }
                }
            }
        } else if (this._value !== null && this._value !== undefined && this._value !== '') {
            // Check if current value is not already in restricted options
            if (!restrictedValues.has(this._value)) {
                const fullOption = optionsMap.get(this._value);
                if (fullOption) {
                    additionalOptions.push(fullOption);
                }
            }
        }

        return [...baseOptions, ...additionalOptions];
    }

    orderedSelectOptions = computed(() => {
        if (this.multiselect() && this.isOpened()) {
            const checkedOptions = this.availableOptions
                .filter((option: SelectOption) => this.isMultiselectChecked(option))
                .sort((a: SelectOption, b: SelectOption) => a.label.localeCompare(b.label));

            const uncheckedOptions = this.availableOptions
                .filter((option: SelectOption) => !this.isMultiselectChecked(option))
                .sort((a: SelectOption, b: SelectOption) => a.label.localeCompare(b.label));

            return [...checkedOptions, ...uncheckedOptions];
        } else {
            const sortedOptions = this.availableOptions.sort((a: SelectOption, b: SelectOption) =>
                a.label.localeCompare(b.label),
            );

            if (this.isOpened()) {
                const selectedOptionIndex = sortedOptions.findIndex(
                    (option: SelectOption) => option.value === this._value,
                );

                if (selectedOptionIndex > -1) {
                    const selectedOption = sortedOptions[selectedOptionIndex];
                    const remainingOptions = sortedOptions.filter(
                        (_: SelectOption, index: number) => index !== selectedOptionIndex,
                    );

                    return [selectedOption, ...remainingOptions];
                }
            }

            return sortedOptions;
        }
    });

    dropdownPosition = computed(() => {
        if (!this.isOpened()) return '';

        const inputElement = this.elementRef.nativeElement;
        const inputRect = inputElement.getBoundingClientRect();
        const spaceBelow = window.innerHeight - inputRect.bottom;
        const spaceAbove = inputRect.top;

        return spaceBelow < this.dropdownHeight && spaceAbove > spaceBelow
            ? DropdownPosition.above
            : DropdownPosition.below;
    });

    filteredItems = computed(() => {
        const query = this.searchQuery().trim();
        let items: SelectOption[];

        if (!query || query === this.selectedLabel) {
            items = this.availableOptions;
        } else {
            items = this.availableOptions.filter(
                (item: SelectOption) =>
                    item.label.toLowerCase().includes(query.toLowerCase()) ||
                    (typeof item.value === 'string' &&
                        item.value.toLowerCase().includes(query.toLowerCase())),
            );
        }
        if (this.isSearch() && this._value) {
            const selectedIndex = items.findIndex(item => item.value === this._value);
            if (selectedIndex > -1) {
                const selectedItem = items[selectedIndex];
                const remainingItems = items.filter((_, index) => index !== selectedIndex);
                return [selectedItem, ...remainingItems];
            }
        }

        return items;
    });

    searchPlaceholder = computed(() => {
        return this.isInForm() && this.isOpened() ? this.selectMessage() : this.placeholder();
    });

    get selectPlaceholder(): string {
        if (this.isOpened() && this.selectMessage()) {
            return this.selectMessage();
        }

        if (this.multiselect() && this.isArray(this._value)) {
            const selectedOptions = this.multiselectNamePipe.transform(
                this._value,
                this.maxDisplayedItems(),
                this.translatePipe.transform(this.moreTranslationKey()),
            );

            return selectedOptions.length
                ? selectedOptions
                : this.selectedLabel || this.placeholder();
        }

        return this.selectedLabel || this.placeholder();
    }

    showRedAsterisk = computed(() => {
        if (this.isSearch()) {
            return this.isRequired() && !this.isOpened() && this.searchQuery().length === 0;
        }

        return (
            this.isRequired() &&
            !this.isOpened() &&
            typeof this._value === 'string' &&
            this._value.length === 0
        );
    });

    get value(): unknown {
        return this._value;
    }

    set value(val: unknown) {
        if (val !== this._value) {
            this._value = val;
            this.onChange(this._value);
            this.onTouch(this._value);
            this.selectionChange.emit(this._value);
        }
    }

    get selectedLabel(): string {
        return (
            this.availableOptions.find((opt: SelectOption) => opt.value === this._value)?.label ||
            ''
        );
    }

    constructor(private elementRef: ElementRef) {
        super();
    }

    ngAfterViewChecked(): void {
        if (this.isOpened()) {
            this.positionDropdown();
        }
        if (!this._hasAutoSelected) {
            this.autoSelectSingleOption();
        }
    }

    private autoSelectSingleOption(): void {
        const available = this.availableOptions;
        if (available.length === 1) {
            const singleOption = available[0];

            // Only auto-select if current value is empty/unset
            if (this.multiselect()) {
                if (this.isArray(this._value) && this._value.length === 0) {
                    this.value = [singleOption];
                    this._hasAutoSelected = true;
                    this.cdr.detectChanges();
                }
            } else {
                if (!this._value || this._value === '') {
                    this.value = singleOption.value;
                    this._hasAutoSelected = true;
                    this.cdr.detectChanges();
                }
            }
        }
    }

    @HostListener('document:click', ['$event'])
    onClickOutside(event: MouseEvent) {
        if (!this.elementRef.nativeElement.contains(event.target)) {
            this.isOpened.set(false);
            this.opened.emit(false);
            this.focusChanged(false);
        }
    }

    @HostListener('document:wheel')
    onMouseWheel() {
        if (this.isOpened()) {
            this.opened.emit(false);
            this.focusChanged(false);
        }
    }

    @HostListener('window:resize')
    onWindowResize() {
        this.isOpened.update(isOpen => isOpen);
    }

    isOptionDisabled(option: SelectOption): boolean {
        if (!this.multiselect() || !this.selectionLimit()) return false;

        const currentSelectionCount = this.isArray(this._value) ? this._value.length : 0;
        const isCurrentlySelected = this.isMultiselectChecked(option);

        if (isCurrentlySelected) return false;

        return currentSelectionCount >= this.selectionLimit()!;
    }

    isSelectAllDisabled(): boolean {
        if (!this.multiselect() || !this.selectionLimit()) return false;

        const currentSelectionCount = this.isArray(this._value) ? this._value.length : 0;
        return currentSelectionCount >= this.selectionLimit()!;
    }

    toggleDropdown(): void {
        if (!this.disabled) {
            this.isOpened.update(isOpen => !isOpen);
            this.opened.emit(this.isOpened());
            this.focusChanged(this.isOpened());
        }
    }

    showDropdown(): void {
        this.isOpened.set(true);
        this.opened.emit(this.isOpened());
    }

    selectOption(option: SelectOption): void {
        this.value = option.value;
        this.isOpened.set(false);
        this.opened.emit(this.isOpened());
        if (this.isSearch()) {
            this.searchQuery.set(option.label);
        }
    }

    writeValue(value: string | string[] | SelectOption[]): void {
        if (this.onChangeInit || (!this.onChangeInit && value !== null && value !== undefined)) {
            // Reset auto-select flag when value is written, so it can auto-select again if cleared
            if (this.multiselect() && Array.isArray(value) && value.length === 0) {
                this._hasAutoSelected = false;
            } else if (!this.multiselect() && (!value || value === '')) {
                this._hasAutoSelected = false;
            }

            if (this.multiselect() && Array.isArray(value)) {
                const isSelectOptionArray =
                    value.length > 0 &&
                    value[0] !== null &&
                    typeof value[0] === 'object' &&
                    'value' in value[0];

                if (isSelectOptionArray) {
                    this.value = value;
                } else {
                    const mappedToSelectOptions = this.mapToSelectOptions(value as string[]);
                    this.value = mappedToSelectOptions;
                }

                this.isOpened.set(true);
                this.isOpened.set(false); // to refresh if there's existing value
            } else {
                if (this.isSearch() && value === '') {
                    return;
                }

                this.value = value;
                this.isOpened.set(true);
                this.isOpened.set(false); // to refresh if there's existing value

                if (this.isSearch()) {
                    const selectedOption = this.availableOptions.find(
                        (el: SelectOption) => el.value === value,
                    );
                    if (selectedOption) this.searchQuery.set(selectedOption?.label);
                }
            }
        }
    }

    handleMultiselectChange(isActive: boolean, option: SelectOption) {
        if (!this.isArray(this._value)) this.value = [];

        if (this.multiselect() && this.isArray(this._value)) {
            if (isActive && !this._value.some(item => item.value === option.value)) {
                const canAddMore =
                    !this.selectionLimit() || this._value.length < this.selectionLimit()!;
                if (canAddMore) {
                    this.value = [...this._value, option];
                }
            } else if (!isActive) {
                this.value = this._value.filter(item => item.value !== option.value);
            }
        }

        if (this.isArray(this._value) && !this._value.length) this.value = '';
    }

    isMultiselectChecked(option: SelectOption): boolean {
        return this.isArray(this._value) && this._value.some(el => el.value === option.value);
    }

    selectAll(): void {
        if (!this.multiselect()) return;

        if (this.selectionLimit()) {
            const limit = this.selectionLimit()!;
            const availableOptions = this.orderedSelectOptions().slice(0, limit);
            this.value = availableOptions;
        } else {
            this.value = [...this.availableOptions];
        }
    }

    unselectAll(): void {
        if (!this.multiselect()) return;

        this.value = [];
    }

    private isArray(value: unknown) {
        return Array.isArray(value);
    }

    private mapToSelectOptions(value: string[]): any[] {
        return value
            .map(element => {
                const existingOption = this.options().find(
                    (option: SelectOption) => option.value === element,
                );
                if (existingOption) return existingOption;
                return null;
            })
            .filter(option => option !== null);
    }

    private positionDropdown(): void {
        const dropdown = this.dropdownElement?.nativeElement;
        if (!dropdown) return;

        const inputElement = this.elementRef.nativeElement;
        const inputRect = inputElement.getBoundingClientRect();
        const dropdownRect = dropdown.getBoundingClientRect();

        const position = this.dropdownPosition();

        dropdown.classList.remove(DropdownPosition.above, DropdownPosition.below);
        if (position) {
            dropdown.classList.add(position);
        }

        let top;
        if (position === DropdownPosition.above) {
            top = inputRect.top - dropdownRect.height;
            if (top < 0) top = 0;
        } else {
            top = inputRect.bottom;
            const maxHeight = Math.min(this.dropdownHeight, window.innerHeight - inputRect.bottom);
            if (top + maxHeight > window.innerHeight) {
                top = Math.max(0, window.innerHeight - maxHeight);
            }
        }

        let left = inputRect.left;
        const width = inputRect.width;
        const rightEdge = left + width;

        if (rightEdge > window.innerWidth) {
            left = Math.max(0, window.innerWidth - width);
        }

        dropdown.style.top = `${top}px`;
        dropdown.style.left = `${left}px`;
        dropdown.style.width = `${width}px`;
        dropdown.style.maxHeight = `${this.dropdownHeight}px`;
    }
}
